<template>
  <div id="pageReleases">
    <v-container grid-list-xl fluid>
      <v-layout row wrap>
        <v-flex xs12>
          <v-card class="release-card">
            <v-card-title class="release-card__title">
              <div>
                <h3 class="release-title">Releases</h3>
                <p class="release-sub">Trigger deployment pipeline by branch, app, and school selection.</p>
              </div>
            </v-card-title>

            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12 md4>
                  <v-select
                    v-model="target"
                    :items="targetOptions"
                    item-text="text"
                    item-value="value"
                    label="Application"
                    dense
                    solo
                    flat
                    @change="handleTargetChanged"
                  ></v-select>
                </v-flex>

                <v-flex xs12 md4>
                  <v-select
                    v-model="branch"
                    :items="branchOptions"
                    label="Branch"
                    dense
                    solo
                    flat
                  ></v-select>
                </v-flex>

                <v-flex xs12 md4>
                  <v-btn
                    color="primary"
                    depressed
                    class="mt-3"
                    :loading="isTriggering"
                    @click="triggerRelease"
                  >
                    <v-icon left small>play_arrow</v-icon>
                    Trigger Release
                  </v-btn>
                </v-flex>

                <v-flex xs12>
                  <v-autocomplete
                    v-model="selectedSchools"
                    :items="schoolOptions"
                    :search-input.sync="schoolSearch"
                    label="Schools"
                    multiple
                    chips
                    deletable-chips
                    dense
                    solo
                    flat
                    :loading="loadingSchools"
                    clearable
                    hint="Select one or multiple schools."
                    persistent-hint
                  ></v-autocomplete>
                </v-flex>

                <v-flex xs12 class="release-actions">
                  <v-btn small text color="primary" @click="selectAllSchools" :disabled="!schoolOptions.length">
                    Select All Schools
                  </v-btn>
                  <v-btn small text color="warning" @click="clearSchools" :disabled="!selectedSchools.length">
                    Clear
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import APIPayment from "@/mixins/APIPayment";
import APIServer from "@/mixins/APIServer";

const DEFAULT_SCHOOL = "edpass_demo_3002";

export default {
  mixins: [APIPayment, APIServer],
  data() {
    return {
      loadingSchools: false,
      isTriggering: false,
      schoolSearch: "",
      target: "backend",
      branch: "main",
      selectedSchools: [DEFAULT_SCHOOL],
      schoolOptions: [],
      targetOptions: [
        { text: "Backend", value: "backend" },
        { text: "Frontend", value: "frontend" }
      ],
      branchMap: {
        backend: ["main", "development"],
        frontend: ["main", "development", "deployment"]
      }
    };
  },
  computed: {
    branchOptions() {
      return this.branchMap[this.target] || [];
    }
  },
  created() {
    this.loadSchools();
  },
  methods: {
    handleTargetChanged() {
      if (!this.branchOptions.includes(this.branch)) {
        this.branch = this.branchOptions[0] || "";
      }
    },
    loadSchools: async function() {
      this.loadingSchools = true;
      try {
        const companies = await this.getCompanies();
        const list = Array.isArray(companies)
          ? companies
          : (companies && companies.data) || [];

        this.schoolOptions = list
          .map(company => company && company.company_no)
          .filter(Boolean)
          .sort();

        if (this.schoolOptions.includes(DEFAULT_SCHOOL)) {
          this.selectedSchools = [DEFAULT_SCHOOL];
        } else if (this.schoolOptions.length) {
          this.selectedSchools = [this.schoolOptions[0]];
        } else {
          this.selectedSchools = [];
        }
      } catch (error) {
        this.$toast.error("Failed to load schools.", "Error", {
          position: "topCenter"
        });
      } finally {
        this.loadingSchools = false;
      }
    },
    selectAllSchools() {
      this.selectedSchools = [...this.schoolOptions];
    },
    clearSchools() {
      this.selectedSchools = [];
    },
    triggerRelease: async function() {
      try {
        if (!this.branch) {
          this.$toast.error("Branch selection is required.", "Error", {
            position: "topCenter"
          });
          return;
        }

        if (!this.selectedSchools.length) {
          this.$toast.error("Select at least one school.", "Error", {
            position: "topCenter"
          });
          return;
        }

        const selectedCount = this.selectedSchools.length;
        const confirmationMessage =
          `Trigger ${this.target} release on '${this.branch}' for ${selectedCount} school` +
          `${selectedCount > 1 ? "s" : ""}?`;

        if (!window.confirm(confirmationMessage)) {
          return;
        }

        this.isTriggering = true;
        const response = await this.triggerDeployPipeline(
          this.branch,
          this.selectedSchools,
          this.target
        );

        const pipelineId =
          response && response.data && response.data.data && response.data.data.id;

        this.$toast.success(
          pipelineId
            ? `Pipeline triggered (ID: ${pipelineId}).`
            : "Pipeline triggered successfully.",
          "Success",
          { position: "topCenter" }
        );
      } catch (error) {
        const message =
          (error &&
            error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          "Failed to trigger release pipeline.";

        this.$toast.error(message, "Error", { position: "topCenter" });
      } finally {
        this.isTriggering = false;
      }
    }
  }
};
</script>

<style scoped>
.release-card {
  border-radius: 12px !important;
}

.release-card__title {
  border-bottom: 1px solid #eceff1;
}

.release-title {
  font-size: 1.25rem;
  margin: 0;
  color: #1f2937;
}

.release-sub {
  margin: 4px 0 0;
  color: #64748b;
}

.release-actions {
  display: flex;
  gap: 8px;
}
</style>
